import { ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'

import { RejectDropZone } from '@/shared/components/dnd'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import type { ProjectSummary } from '@/shared/types/domain'

import { PriorityRow } from './PriorityRow'

export const ONGOING_ZONE_ID = 'zona-permanentes'

/**
 * El motivo del rechazo viaja como CLAVE, no como texto: lo traduce quien lo
 * muestra —la zona o el toast de la pantalla—, con el idioma de quien mira.
 */
export const ONGOING_REJECT_KEY = 'priorities.ongoing.rejectReason'

export interface OngoingSectionProps {
  projects: readonly ProjectSummary[]
  /** Índice nominal de la sección: va después de la última fila priorizada. */
  index: number
  className?: string
}

/**
 * Los proyectos permanentes, al final y colapsables.
 *
 * **Sin número y no arrastrables**: rankean en su propio scope (plan 2.8), y
 * mezclarlos con los proyectos con entrega rompe la única pregunta que esta
 * pantalla contesta, que es qué se desarrolla primero.
 *
 * La sección igual se registra como zona de drop para poder **rechazar** el drop
 * y decir por qué. Si no estuviera registrada, soltar ahí no haría nada y
 * parecería un bug.
 */
export function OngoingSection({ projects, index, className }: OngoingSectionProps) {
  const t = useT()
  // Abierta por default: son pocos y no verlos es peor que el espacio que ocupan.
  const [open, setOpen] = useState(true)

  if (projects.length === 0) return null

  return (
    <RejectDropZone
      id={ONGOING_ZONE_ID}
      reason={t(ONGOING_REJECT_KEY)}
      index={index}
      className={cn('mt-3', className)}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-[var(--radius-control)] px-2 py-1.5 text-2xs text-text-2 hover:bg-surface-2 hover:text-text-1"
      >
        {open ? (
          <ChevronDown size={13} aria-hidden="true" />
        ) : (
          <ChevronRight size={13} aria-hidden="true" />
        )}
        {t('priorities.ongoing.title')}
        <span
          className="num text-text-3"
          aria-label={t('priorities.ongoing.count', { count: projects.length })}
        >
          {projects.length}
        </span>
        <span className="ml-auto text-text-3">{t('priorities.noPriority')}</span>
      </button>

      {open ? (
        <div className="hairline overflow-hidden rounded-[var(--radius-card)]">
          {projects.map((project) => (
            <PriorityRow
              key={project.id}
              project={project}
              position={null}
              // Sin handle: un permanente no se arrastra. No se deshabilita un
              // control que no debería existir, directamente no se monta.
              handle={<span className="w-4 shrink-0" aria-hidden="true" />}
            />
          ))}
        </div>
      ) : null}
    </RejectDropZone>
  )
}
